<script lang="ts" setup>
import type { ChartLineData } from '~/components/nrjx/chart/line.vue';
import type { Objective } from '../../types';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Composables
const dayjs = useDayjs();
dayjs.extend(utc);
dayjs.extend(timezone);
const { getTailwindColor } = useTailwind();
const { options } = ObjectiveOptionChart();
const { getReferenceYearConsumption, getCurrentYearConsumption } = useOverviewObjectives();

// Props
const props = defineProps<{
  objective: Objective;
  unit: 'kwh' | 'euro';
}>();

// Data
const loading = ref(false);
const referenceYearConsumptionData = ref<ApiConsumptionResponse['items'] | null>(null);
const currentYearConsumptionData = ref<ApiConsumptionResponse['items'] | null>(null);
const timestamps = ref<Date[]>([]);
const datasets = ref<ChartLineData['datasets']>([]);

// Computed
const chartOptions = computed(() => {
  return options({
    milestones: allMilestones.value,
    unit: props.unit,
    referenceYearConsumption: referenceYearConsumptionData.value,
    currentYearConsumption: currentYearConsumptionData.value,
    reference_year: props.objective.reference_year,
  });
});

const unitInText = computed(() => {
  switch (props.unit) {
    case 'kwh':
      return 'kWh';
    case 'euro':
      return '€';
    default:
      return 'kWh';
  }
});

const allMilestones = computed(() => {
  return [
    { timestamp: dayjs.tz(props.objective.start, 'utc').tz('Europe/Paris').toDate(), consumption_target: 0, cost_target: 0, co2_target: 0 },
    ...props.objective.objective_milestones.map((item) => ({
      timestamp: dayjs.tz(item.timestamp, 'utc').tz('Europe/Paris').toDate(),
      consumption_target: item.consumption_target,
      cost_target: item.cost_target,
      co2_target: item.co2_target,
    })),
    {
      timestamp: dayjs.tz(props.objective.end, 'utc').tz('Europe/Paris').toDate(),
      consumption_target: props.objective.target,
      cost_target: props.objective.cost_target,
      co2_target: 0,
    },
  ];
});

// Methods
const loadData = async () => {
  const areaId = areaStore().areas.find((area) => area.meters.find((meter) => meter.id === props.objective.meter_id))?.id;
  if (!areaId || !props.objective.meter_id) {
    return { referenceYearConsumption: null, currentYearConsumption: null };
  }

  const promises = [
    getCurrentYearConsumption({
      meterId: props.objective.meter_id,
      areaId: areaId,
    }),
  ];

  if (props.objective.reference_year) {
    promises.push(
      getReferenceYearConsumption({
        meterId: props.objective.meter_id,
        areaId: areaId,
        referenceYear: props.objective.reference_year,
      }),
    );
  }

  const response = await Promise.all(promises);

  return {
    currentYearConsumption: response?.at(0) ?? null,
    referenceYearConsumption: props.objective.reference_year ? (response?.at(1) ?? null) : null,
  };
};

const setData = async () => {
  loading.value = true;
  const { referenceYearConsumption, currentYearConsumption } = await loadData();
  if (!currentYearConsumption) return;

  //const co2PerKwh = useCO2().getCo2byKWhByMeterId(props.objective.meter_id);
  referenceYearConsumptionData.value = referenceYearConsumption!.items!;
  currentYearConsumptionData.value = currentYearConsumption!.items!;
  timestamps.value = allMilestones.value.map((item) => item.timestamp);

  datasets.value = [
    {
      label: 'Current year',
      data: [0].concat(
        currentYearConsumptionData.value!.map((item) => {
          switch (props.unit) {
            case 'kwh':
              return item.consumption.quantity;
            case 'euro':
              return Number(item.consumption.cost);
            //case 'co2':
            //  return Number(item.consumption.quantity * co2PerKwh);
          }
        }),
      ),

      color: getTailwindColor('emerald-500'),
    },
    {
      label: 'Objective',
      data: allMilestones.value.map((item) => {
        switch (props.unit) {
          case 'kwh':
            return item.consumption_target;
          case 'euro':
            return item.cost_target;
          //case 'co2':
          //  return item.co2_target;
        }
      }),
      color: getTailwindColor('blue-600'),
      dashed: true,
    },
  ];

  if (referenceYearConsumptionData.value) {
    datasets.value.unshift({
      label: 'Reference year',
      data: [0].concat(
        referenceYearConsumptionData.value.map((item) => {
          switch (props.unit) {
            case 'kwh':
              return Number(item.consumption.quantity);
            case 'euro':
              return Number(item.consumption.cost);
            //case 'co2':
            //  return Number(item.consumption.quantity * co2PerKwh);
          }
        }),
      ),
      color: getTailwindColor('orange-400'),
    });
  }

  loading.value = false;
};

// Lifecycle
watch(
  [() => props.objective, () => props.unit],
  () => {
    setData();
  },
  {
    deep: true,
    immediate: true,
  },
);
</script>
<template>
  <nrjx-chart-old-line
    :title="unitInText"
    :options="chartOptions"
    :loading="loading"
    :disabled-zoom="true"
    :data="{
      labels: timestamps,
      datasets: datasets,
    }"
  />
</template>
